import DeleteIcon from '@mui/icons-material/Close'
import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { boldBlueHeader } from './styles.ts'
import { useTheme } from '@mui/material'

export interface BidFormState {
  rate: string
  treasuryRate: string
  notes: string
}

export interface BidFieldErrors {
  rate?: string
  treasuryRate?: string
}

interface Props {
  form: BidFormState
  errors?: BidFieldErrors
  onFieldChange: (field: keyof BidFormState, value: string) => void
  onDelete: () => void
}

export default function BidForm({
  form,
  errors,
  onFieldChange,
  onDelete,
}: Props) {
  const theme = useTheme()
  const [hoverDelete, setHoverDelete] = useState(false)
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: hoverDelete ? 'error.main' : theme.palette.grey[200],
        borderRadius: 1,
        px: 2,
        py: 1,
        mt: 0,
        mb: 0,
        minHeight: 225,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        transition: 'border-color 0.2s, background-color 0.2s',
        backgroundColor: hoverDelete
          ? 'rgba(211,47,47,0.04)'
          : 'background.paper',
        '&:hover': {
          boxShadow: hoverDelete
            ? '0 0 8px rgba(211,47,47,0.4)'
            : '0 1px 3px rgba(0,0,0,0.12)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0,
        }}
      >
        <Typography variant="h6" color={theme.palette.primary.main}>
          Bid
        </Typography>
        <IconButton
          size="small"
          onClick={onDelete}
          onMouseEnter={() => setHoverDelete(true)}
          onMouseLeave={() => setHoverDelete(false)}
          sx={{
            ml: 1,
            color: hoverDelete ? 'error.main' : 'action.active',
            transition: 'color 0.2s, background-color 0.2s',
            '&:hover': {
              bgcolor: 'error.light',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Table size="small" sx={{ mb: 1 }}>
        <TableBody>
          <TableRow>
            <TableCell
              sx={{
                ...boldBlueHeader,
                whiteSpace: 'nowrap',
                pr: 2,
                width: 130,
              }}
            >
              Rate
            </TableCell>
            <TableCell sx={{ pl: 2 }}>
              <TextField
                size="small"
                sx={{ minWidth: 120 }}
                type="number"
                value={form.rate}
                onChange={(e) => onFieldChange('rate', e.target.value)}
                error={!!errors?.rate}
                helperText={errors?.rate}
                slotProps={{
                  htmlInput: {
                    step: '0.01',
                    min: 0,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                ...boldBlueHeader,
                whiteSpace: 'nowrap',
                pr: 2,
                width: 130,
              }}
            >
              Treasury Rate
            </TableCell>
            <TableCell sx={{ pl: 2 }}>
              <TextField
                size="small"
                sx={{ minWidth: 120 }}
                type="number"
                value={form.treasuryRate}
                onChange={(e) => onFieldChange('treasuryRate', e.target.value)}
                error={!!errors?.treasuryRate}
                helperText={errors?.treasuryRate}
                slotProps={{
                  htmlInput: {
                    step: '0.01',
                    min: 0,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Additional Notes"
        sx={{ mt: 1 }}
        value={form.notes}
        onChange={(e) => onFieldChange('notes', e.target.value)}
      />
    </Box>
  )
}
